import endpoint from 'utils/endpoint';
import { fetchWithRetry } from 'utils/fetchWithRetry';
import { HttpRequestMethodTypes } from 'types';
import { getGoSearchHost, isServerSide } from 'utils/host-config';
import { SearchPredictionResponse } from '@zoocasa/go-search';
import type { SearchPredictionsParams, SearchPredictionsApiResponse } from './types';
import type { FetchOptions } from 'types/fetchWithRetry';

//#region V1

/**
 * Fetch search predictions from the go-search service V1 endpoint.
 * 
 * @param params Search parameters including query, country, and (optional) limit
 * @param host Request host
 * @returns SearchPredictionsApiResponse or null
 */
export async function fetchSearchPredictionsV1(
  params: SearchPredictionsParams,
  host: string = getGoSearchHost(isServerSide())
): Promise<SearchPredictionsApiResponse | null> {
  try {
    const searchPredictionsUrl = '/api/search-predictions';

    const response = await endpoint<SearchPredictionsApiResponse>(
      searchPredictionsUrl,
      HttpRequestMethodTypes.GET,
      params,
      {}, // headers
      undefined, // body
      host
    );

    return response;
  } catch (error) {
    console.error('Error fetching search predictions V1:', error);
    return null;
  }
}

//#endregion

//#region V2

/**
 * Fetch search predictions from the go-search service V2 endpoint 
 * @param params Search parameters including search query and country. Note: Limit parameter was removed in V2, 
 * the number of results returned is now determined by the endpoint, based on the query type (province, city/neighbourhood, address etc.)
 * @param signal (optional) AbortSignal to cancel the request
 * @param host  (optional) Request host
 * @returns SearchPredictionResponse
 */
export async function fetchSearchPredictionsV2(
  params: SearchPredictionsParams,
  signal?: AbortSignal,
  host: string = getGoSearchHost(isServerSide())
): Promise<SearchPredictionResponse | null> {
  const searchPredictionsUrl = '/api/v2/search-predictions';
  
  const queryParams = new URLSearchParams({
    query: params.query,
    country: params.country,
  });
  
  const fullUrl = `${host}${searchPredictionsUrl}?${queryParams.toString()}`;

  try {
    const options: FetchOptions = {
      method: HttpRequestMethodTypes.GET,
      signal,
    };
    
    const response = await fetchWithRetry(fullUrl, options);

    if (!response.ok) {
      console.error(`fetch search predictions: ${response.status} ${response.statusText} ${response.type}`);
      return null;
    }

    const content = await response.blob();
    const buffer = await content.arrayBuffer();
    const decodedResponse = SearchPredictionResponse.decode(new Uint8Array(buffer));
    return decodedResponse;

  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.debug('fetch search predictions request was aborted');
    } else {
      console.error('failed to fetch search predictions: %s', error);
    }
    return null;
  }
}

//#endregion
